import React, { useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import OllieIcon from 'src/components/icons/OllieIcon'
import useOllie from 'src/hooks/useOllie'
import useOllieFindings from 'src/hooks/useOllieFindings'
import { OLLIE_FINDING_SURFACES } from 'src/utils/ollieFindingsApi'
import { OLLIE_FINDING_ACTION_MODES } from 'src/utils/ollieFindingCards'
import {
  buildOllieNoticedRows,
  OLLIE_NOTICED_COPY,
  ollieNoticedChecksFor,
  ollieNoticedRowsKey,
  ollieNoticedTakesAnyCheck,
} from 'src/utils/ollieNoticedRows'
import styled from 'styled-components/macro'
import OllieAskLink from './OllieAskLink'
import { useOllieAssistant } from './OllieAssistantContext'

/**
 * Slim "Ollie noticed" rows on the screen where the problem is. Renders
 * nothing when Ollie has nothing for this page (no quiet-day line here;
 * the panel says quiet days out loud).
 *
 * @param {{ page: string, className?: string, surface?: string,
 *   excludeCheckIds?: string[], quiet?: boolean,
 *   onRowsChange?: (rows: object[]) => void }} props
 *   `page` from `OLLIE_NOTICED_PAGES`. `quiet`: olive edge, no lime (Progress
 *   lime budget: the job CTA is the only lime). `onRowsChange`: the rows
 *   actually shown (empty when Ollie is off), for pages that drop a
 *   duplicate button.
 */
const OllieNoticedRows = ({
  page,
  className,
  surface = OLLIE_FINDING_SURFACES.FEATURE_PAGE,
  excludeCheckIds = [],
  quiet = false,
  onRowsChange,
}) => {
  const history = useHistory()
  const location = useLocation()
  const { enabled } = useOllie()
  const { openOllieWithFinding } = useOllieAssistant()
  const anyCheck = ollieNoticedTakesAnyCheck(page)
  const checkIds = ollieNoticedChecksFor(page)
  const {
    venueId,
    asOf,
    findings,
    forChecks,
    markShown,
    markClicked,
    dismiss,
  } = useOllieFindings({
    autoLoad: checkIds.length > 0,
    checkIds: anyCheck ? null : checkIds,
  })
  const excludeKey = (excludeCheckIds || []).join('|')
  const shownRef = useRef(new Set())

  const rows = useMemo(
    () =>
      buildOllieNoticedRows({
        findings: anyCheck ? findings : forChecks,
        page,
        venueId,
        asOf,
        currentPath: `${location.pathname}${location.search}`,
        excludeCheckIds: excludeKey ? excludeKey.split('|') : [],
      }),
    [
      anyCheck,
      findings,
      forChecks,
      page,
      venueId,
      asOf,
      location.pathname,
      location.search,
      excludeKey,
    ],
  )
  const visibleRows = useMemo(() => (enabled ? rows : []), [enabled, rows])

  useEffect(() => {
    visibleRows.forEach(row => {
      const key = `${row.checkId}:${row.finding.fingerprint || ''}`
      if (shownRef.current.has(key)) return
      shownRef.current.add(key)
      markShown(row.finding, surface)
    })
  }, [visibleRows, markShown, surface])

  const rowsKey = ollieNoticedRowsKey(visibleRows)
  const reportedKeyRef = useRef(null)
  useEffect(() => {
    if (!onRowsChange || reportedKeyRef.current === rowsKey) return
    reportedKeyRef.current = rowsKey
    onRowsChange(visibleRows)
  }, [rowsKey, visibleRows, onRowsChange])

  if (!visibleRows.length) return null

  const handleAction = row => {
    markClicked(row.finding, surface)
    if (row.action.mode === OLLIE_FINDING_ACTION_MODES.NAVIGATE) {
      history.push(row.action.path)
      return
    }
    openOllieWithFinding(row.checkId, { startAction: row.action })
  }

  return (
    <Rows className={className} data-testid={`ollie-noticed-${page}`}>
      {visibleRows.map(row => (
        <Row
          key={row.checkId}
          data-testid='ollie-noticed-row'
          data-chrome={quiet ? 'quiet' : undefined}
          $quiet={quiet}
        >
          <Label>
            <OllieIcon size={16} />
            {OLLIE_NOTICED_COPY.label}
          </Label>
          <TitleBlock>
            <Title>{row.title}</Title>
            {row.noticed && (
              <Noticed data-testid='ollie-noticed-row-detail'>
                {row.noticed}
              </Noticed>
            )}
          </TitleBlock>
          {row.asOfLabel && <Meta>{row.asOfLabel}</Meta>}
          <Spacer />
          {row.action && (
            <ActionButton type='button' onClick={() => handleAction(row)}>
              {row.action.label}
            </ActionButton>
          )}
          <WhyLink
            checkId={row.checkId}
            ask='why'
            label={OLLIE_NOTICED_COPY.why}
            onOpen={() => markClicked(row.finding, surface)}
          />
          <DismissButton
            type='button'
            data-testid='ollie-noticed-row-dismiss'
            aria-label={`${OLLIE_NOTICED_COPY.dismiss}: ${row.title}`}
            onClick={() => dismiss(row.finding, surface)}
          >
            {OLLIE_NOTICED_COPY.dismiss}
          </DismissButton>
        </Row>
      ))}
    </Rows>
  )
}

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 0 16px;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-left: 3px solid
    ${({ theme, $quiet }) =>
      $quiet ? theme.colours.oliveGrey : theme.colours.lettuceGreen};
  border-radius: ${({ $quiet }) => ($quiet ? '12px' : '8px')};
  background: ${({ theme }) => theme.colours.mayoWhite};
`

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const TitleBlock = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
`

const Title = styled.span`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 14px;
  font-weight: 600;
`

const Noticed = styled.span`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
`

const Meta = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
`

const Spacer = styled.span`
  flex: 1 1 auto;
`

const ActionButton = styled.button`
  padding: 4px 12px;
  border: 1px solid ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colours.bgGrey};
  }
`

const DismissButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colours.bbqBlack};
  }
`

const WhyLink = styled(OllieAskLink)`
  margin: 0;
  font-size: 13px;
`

export default OllieNoticedRows
